/* oxlint-disable react-doctor/rendering-hydration-mismatch-time */
import FeatureFlagged from 'components/featureflag/FeatureFlagged';
import DynamicSelectInput from 'components/forms/inputs/rhf-inputs/DynamicSelectInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import { activeCasesFilter } from 'components/tables/list-table/filter-pieces/filterConstants';
import { isBefore } from 'date-fns';
import { useEffect, useMemo } from 'react';
import { useForm, useFormState, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useGetMinAssignmentsListWithFiltersQuery } from 'store/api/assignments';
import { useCanReadMatterAssignments } from 'utils/permissionUtil';
import { useGetMattersListQuery, useLazyGetMattersListQuery } from 'store/api/matters';
import { MatterAssignmentMinDto } from 'types/assignments';
import { ContactMinDto } from 'types/contacts';
import { FeatureFlag } from 'types/features';
import { MatterListItemMin } from 'types/matters';
import { NamedEntityMinDto } from 'types/other';
import { t } from 'utils/getTranslations';
import { renderMatterIndicators, renderMatterOptionWithRestrictedSelection } from 'utils/matterOptionRenderer';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { ModalFooter } from '../ModalFooter';
import { ModalHeader } from '../ModalHeader';

export interface SwitchMatterModalResult {
    client: { id: number; name: string } | null;
    matter: MatterListItemMin | null;
    matterAssignment: NamedEntityMinDto | null;
}

interface Props {
    titleId?: string;
    onSubmit: (value: SwitchMatterModalResult) => Promise<void> | void;
    onCancel: () => void;
    entityIds: Set<number>;
    entitySingular: string;
    entityPlural: string;
    showAssignmentSelect: boolean;
    allowClosedAssignments?: boolean;
}

const isAssignmentClosed = (option: MatterAssignmentMinDto) =>
    !!option.endDate && !isBefore(new Date(), new Date(option.endDate));

const defaultValues: SwitchMatterModalResult = {
    client: null,
    matter: null,
    matterAssignment: null,
};

const SwitchMatterModal = (props: Props) => {
    const { onCancel, onSubmit, titleId = 'contacts.about.title', entityIds, allowClosedAssignments = false } = props;
    const isPartyNumberUsageEnabled = useIsFeatureEnabled(FeatureFlag.PARTY_NUMBER_USAGE);

    const { handleSubmit, control, setValue } = useForm<SwitchMatterModalResult>({
        defaultValues: defaultValues,
        values: defaultValues,
    });

    const { isValid } = useFormState({ control });

    const matter = useWatch({ control, name: 'matter' });

    const { data: currentMatterQueryData } = useGetMattersListQuery(
        { pageSize: 1, page: 0, filters: `id==${matter?.id}` },
        { skip: !matter?.id }
    );

    const currentMatterData = useMemo(() => {
        return currentMatterQueryData?.content.at(0);
    }, [currentMatterQueryData]);

    useEffect(() => {
        if (!matter?.id) {
            setValue('client', null);
        } else if (currentMatterData) {
            setValue('client', currentMatterData.clientParty);
        }
    }, [currentMatterData, setValue, matter]);

    const canReadMatterAssignments = useCanReadMatterAssignments();

    const {
        data: currentMatterAssingments,
        isLoading: isLoadingMatterAssignments,
        isFetching: isFetchingMatterAssignments,
    } = useGetMinAssignmentsListWithFiltersQuery(
        {
            filters: `matter.id==(${matter?.id || '0'})`,
            sort: 'id,desc',
            pageSize: 1500,
        },
        { skip: !matter || !props.showAssignmentSelect || !canReadMatterAssignments }
    );

    const isLoading = isLoadingMatterAssignments || isFetchingMatterAssignments;

    const matterAssignmentOptions = useMemo<MatterAssignmentMinDto[]>(() => {
        if (!matter || isLoading) {
            return [];
        }
        return currentMatterAssingments?.content ?? [];
    }, [matter, isLoading, currentMatterAssingments]);

    const areAllAssignmentsDisabled = useMemo(() => {
        if (allowClosedAssignments || !matterAssignmentOptions.length) {
            return false;
        }
        return matterAssignmentOptions.every(isAssignmentClosed);
    }, [allowClosedAssignments, matterAssignmentOptions]);

    const onSave = async () => {
        await handleSubmit((data) => onSubmit(data))();
    };

    return (
        <>
            <ModalHeader kit title={t(titleId)} onCancel={onCancel} />
            <div className="new:px-6 new:py-6 h-full overflow-y-auto px-8 py-[38px] max-sm:px-4 max-sm:py-6">
                <div className="flex flex-col gap-4">
                    <p className="text-body1">
                        <FormattedMessage
                            id="invoices.move-to-another-case-description"
                            values={{
                                count: String(entityIds.size),
                                entitySingular: props.entitySingular,
                                entityPlural: props.entityPlural,
                            }}
                        />
                    </p>
                    <DynamicSelectInput
                        setValue={setValue}
                        control={control}
                        label="timecards.form.case"
                        required
                        name="matter"
                        onChange={() => setValue('matterAssignment', null, { shouldDirty: false })}
                        paginated={{
                            fetch: useLazyGetMattersListQuery,
                            searchField: ['fullName', 'clientParty.name', 'clientParty.partyNumber'],
                            filters: activeCasesFilter,
                            sort: ['clientParty.name,asc', 'name,asc'],
                        }}
                        groupByField="clientParty.name"
                        getOptionLabel="fullName"
                        getOptionIdentifier="id"
                        placeholder="timecards.form.select-case"
                        placeholderOnFocus="timecards.form.search-case-by"
                        getSelectedValueStartAdornment={renderMatterIndicators}
                        customOptionRenderer={(option, props, getValue) =>
                            renderMatterOptionWithRestrictedSelection(option, props, getValue)
                        }
                        getGroupStartAdornment={
                            isPartyNumberUsageEnabled
                                ? (_group, options) => {
                                      if (!options || options.length === 0) {
                                          return null;
                                      }
                                      const firstOption = options[0];
                                      if (!firstOption?.clientParty) {
                                          return null;
                                      }
                                      const partyNumber = firstOption.clientParty.partyNumber?.trim();
                                      if (partyNumber && partyNumber.length > 0) {
                                          return `[${partyNumber}]`;
                                      }
                                      return null;
                                  }
                                : undefined
                        }
                    />
                    <SelectInput
                        setValue={setValue}
                        control={control}
                        label="timecards.form.client"
                        name="client"
                        options={[] as ContactMinDto[]}
                        disabled
                        getOptionLabel="name"
                        getOptionIdentifier="id"
                        placeholder={' '}
                    />
                    {matterAssignmentOptions.length > 0 ? (
                        <FeatureFlagged action="show" flag={FeatureFlag.CASE_ASSIGNMENT_ENABLED}>
                            <SelectInput
                                control={control}
                                setValue={setValue}
                                label="timecards.form.assignment"
                                options={matterAssignmentOptions}
                                getOptionIdentifier="id"
                                getOptionLabel="name"
                                required={!allowClosedAssignments && !areAllAssignmentsDisabled}
                                name="matterAssignment"
                                getOptionDisabled={(option) => !allowClosedAssignments && isAssignmentClosed(option)}
                            />
                        </FeatureFlagged>
                    ) : null}
                </div>
            </div>
            <ModalFooter
                kit
                onCancel={onCancel}
                onSave={() => {
                    void onSave();
                }}
                disableSave={!isValid || isLoading}
            />
        </>
    );
};

export default SwitchMatterModal;
